import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import './Home.css';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [currentSlide, setCurrentSlide] = useState(0);

  // Counter animation function
  useEffect(() => {
    const animateCounters = () => {
      const counters = document.querySelectorAll('.stat-number[data-target]');

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const counter = entry.target;
              const target = parseInt(counter.getAttribute('data-target'));
              const duration = 2000;
              const increment = target / (duration / 16);
              let current = 0;

              const updateCounter = () => {
                if (current < target) {
                  current += increment;
                  counter.textContent = Math.floor(current);
                  requestAnimationFrame(updateCounter);
                } else {
                  counter.textContent = target;
                }
              };

              updateCounter();
              observer.unobserve(counter);
            }
          });
        },
        { threshold: 0.5 }
      );

      counters.forEach((counter) => {
        observer.observe(counter);
      });
    };

    const timer = setTimeout(animateCounters, 100);
    return () => clearTimeout(timer);
  }, []);

  const bannerImages = [
    '/assets/img/universities/universities-1.jpg',
    '/assets/img/universities/universities-2.jpg',
    '/assets/img/universities/universities-3.jpg',
    '/assets/img/universities/universities-4.jpg',
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % bannerImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [bannerImages.length]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await apiService.createEnquiry(formData);
      toast.success(
        'Thank you! Your enquiry has been submitted successfully. We will contact you soon.'
      );

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      toast.error(error.message || 'Failed to submit enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const universities = [
    {
      name: 'Georgia',
      description:
        'Georgia delivers top-tier medical education through advanced infrastructure, seasoned faculty, and global accreditation, positioning it as a prime choice for healthcare students pursuing international careers',
      logo: '/assets/img/universities/universities-1.jpg',
    },
    {
      name: 'Egypt',
      description:
        'Egypt provides excellent medical education with advanced facilities, experienced faculty, and globally recognized programs, preparing students for successful careers in healthcare and medical research.',
      logo: '/assets/img/universities/universities-2.jpg',
    },
    {
      name: 'Kyrgyzstan',
      description:
        'Kyrgyzstan offers affordable and globally recognized medical education with modern facilities and practical training, making it a preferred destination for aspiring medical professionals worldwide.',
      logo: '/assets/img/universities/universities-3.jpg',
    },
    {
      name: 'Moldova',
      description:
        'Moldova provides high-quality medical education with modern facilities, practical training, and international recognition, preparing students for successful careers in the healthcare industry.',
      logo: '/assets/img/universities/universities-4.jpg',
    },
  ];

  return (
    <div className='home'>
      {/* 1. Banner with Contact Form */}
      <section className='hero-banner'>
        <div className='hero-background'>
          {bannerImages.map((image, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{ backgroundImage: `url(${image})` }}
            ></div>
          ))}
        </div>
        <div className='hero-overlay'></div>
        <div className='hero-content'>
          <div className='hero-text'>
            <div className='experience-badge'>24 Years of experience in the industry</div>
            <h1 style={{ fontSize: '1 rem' }}>Your boarding pass to a dream career</h1>
            <p>
              DestNation Education is a Dubai-based company specializing in helping ambitious
              students secure admission to the world's most admired universities, with a core focus
              on Medical education.
            </p>
            <div className='hero-cta-buttons'>
              <a href='/universities' className='hero-cta-primary'>
                Explore Universities
              </a>
              <a href='/about-us' className='hero-cta-secondary'>
                Learn More
              </a>
            </div>
          </div>
          <div className='hero-form'>
            <h3>Get Free Consultation</h3>
            <form onSubmit={handleSubmit}>
              <div className='form-row'>
                <input
                  type='text'
                  name='name'
                  placeholder='Full Name'
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
                <input
                  type='email'
                  name='email'
                  placeholder='Email Address'
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className='form-row'>
                <input
                  type='tel'
                  name='phone'
                  placeholder='Phone Number'
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
                <select name='subject' value={formData.subject} onChange={handleChange} required>
                  <option value=''>Select Subject</option>
                  <option value='admission'>Admission Inquiry</option>
                  <option value='consultation'>Free Consultation</option>
                  <option value='visa'>Visa Assistance</option>
                  <option value='scholarship'>Scholarship Information</option>
                </select>
              </div>
              <textarea
                name='message'
                placeholder='Your Message'
                value={formData.message}
                onChange={handleChange}
                rows='3'
                required
              ></textarea>
              <button type='submit' className='submit-btn' disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Get Free Consultation'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* 2. Unlocking Global Opportunities */}
      <section className='global-opportunities'>
        <div className='container'>
          <div className='content-wrapper'>
            <div className='text-content'>
              <h2>Unlocking Global Opportunities</h2>
              <p>
                Destnation Education helps students migrate for higher education, ensuring
                professionalism and transparency through a well-curated system that provides
                reliable guidance for a smooth and trustworthy migration process.
              </p>
              <p>
                Embark on a transformative journey with DestNation Education, guiding students
                through international education, unlocking global opportunities, and ensuring access
                to top institutions with expert support throughout the process.
              </p>
            </div>
            <div className='image-content'>
              <img src='/assets/img/about/about-1.jpg' alt='Global Education' />
            </div>
          </div>

          <div className='services-section'>
            <div className='service-card'>
              <div className='service-icon'>
                <i className='fas fa-graduation-cap'></i>
              </div>
              <h3>Expert Consultation</h3>
              <p>
                Receive professional guidance for a seamless international education journey,
                ensuring informed decisions and access to top institutions worldwide.
              </p>
            </div>
            <div className='service-card'>
              <div className='service-icon'>
                <i className='fas fa-handshake'></i>
              </div>
              <h3>Trusted Advisory</h3>
              <p>
                Trusted Advisory offers expert guidance and personalized support to ensure
                successful medical education abroad experiences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Free Online Counseling */}
      <section className='free-counseling'>
        <div className='container'>
          <div className='content-wrapper reverse'>
            <div className='image-content'>
              <img src='/assets/img/about/about-2.jpg' alt='Free Counseling' />
            </div>
            <div className='text-content'>
              <h2>Free Online Counseling</h2>
              <p className='help-text'>We are ready to help you</p>
              <p>
                DestNation Education is a Dubai-based company that assists students in securing
                admissions to top universities, with a special focus on medical education.
              </p>
              <a
                href='/contact-us'
                className='cta-btn'
                style={{ background: 'linear-gradient(135deg, #df1575, #f04a9c)' }}
              >
                Book Free Session
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Choose Your Visa Features */}
      <section className='visa-features'>
        <div className='container'>
          <div className='section-header'>
            <h2>Choose Your Visa</h2>
            <p>Features Generate Great Visa</p>
          </div>
          <div className='features-grid'>
            <div className='feature-card'>
              <div className='feature-icon'>
                <i className='fas fa-chalkboard-teacher'></i>
              </div>
              <h3>Bridge Class</h3>
              <p>
                Destnation provides bridge class to each student who joins for the medical education
                abroad with us. This bridge class will help the students to effectively bridge the
                gap they feel while pursuing their education in a different atmosphere.
              </p>
            </div>
            <div className='feature-card'>
              <div className='feature-icon'>
                <i className='fas fa-user-md'></i>
              </div>
              <h3>Medical Gown and Stethoscope</h3>
              <p>
                We provide a comprehensive medical kit, including a stethoscope and medical gown, to
                every student who processes their admission through our company, ensuring they are
                fully prepared for their medical studies and equipped for their education abroad.
              </p>
            </div>
            <div className='feature-card'>
              <div className='feature-icon'>
                <i className='fas fa-book-medical'></i>
              </div>
              <h3>Medical Books</h3>
              <p>
                We supply first-year medical books to all students who secure admission through our
                company.
              </p>
            </div>
            <div className='feature-card'>
              <div className='feature-icon'>
                <i className='fas fa-language'></i>
              </div>
              <h3>Language Training</h3>
              <p>
                We offer Arabic language training to students heading to Egypt, helping them with
                both daily and medical language usage.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Favourite Destination - Top Universities */}
      <section className='top-universities'>
        <div className='container'>
          <div className='section-header'>
            <h2>Favourite Destination</h2>
            <p>Top Universities Partnered With Us</p>
          </div>
          <div className='universities-grid'>
            {universities.map((university, index) => (
              <div key={index} className='university-card'>
                <div className='university-logo'>
                  <img src={university.logo} alt={university.name} />
                </div>
                <div className='university-info'>
                  <h3>{university.name}</h3>
                  <p>{university.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Major Universities Associated */}
      <section className='major-universities'>
        <div className='container'>
          <div className='section-header'>
            <h2>Major Universities Associated With Us</h2>
            <p>
              We serve admissions to more than 600 universities across the globe. All of them are
              recognized and reputed universities. We guide students to get admitted to universities
              at their choice and criteria.
            </p>
          </div>
          <div className='universities-collage'>
            <img
              src='/assets/img/universities/universities-collage.jpg'
              alt='Universities Collage'
            />
          </div>
        </div>
      </section>

      {/* 7. Seamless Migration Services */}
      <section className='migration-services'>
        <div className='container'>
          <div className='content-wrapper'>
            <div className='text-content'>
              <h2>From your home to top universities worldwide</h2>
              <h3>Seamless Migration Services</h3>
              <p>
                At DestNation, we redefine the migration experience by bringing our services to your
                doorstep. Say goodbye to the complexities of migration as our team ensures a
                seamless transition from your home country to prestigious universities abroad. Our
                personalized approach guarantees a smooth and stress-free relocation, allowing you
                to focus on your academic pursuits.
              </p>
            </div>
            <div className='video-content'>
              <div className='video-wrapper'>
                <iframe
                  src="https://www.youtube-nocookie.com/embed/lSqcDDPLlrQ?enablejsapi=1&controls=1&modestbranding=1&rel=0&showinfo=0"
                  title="DestNation Services"
                  style={{ width: "100%", height: "315px", border: 0 }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Testimonials */}
      <section className='testimonials'>
        <div className='container'>
          <div className='section-header'>
            <h2>Hear From Our Students</h2>
            <p>Success stories from students who achieved their dreams with DestNation</p>
          </div>
          <div className='testimonials-grid'>
            <div className='fb-post-card'>
              <iframe
                src='https://www.facebook.com/plugins/post.php?href=https%3A%2F%2Fwww.facebook.com%2Fnidhamehtaj%2Fposts%2F4239321146394612&show_text=true&width=500'
                width='100%'
                height='400'
                style={{ border: 'none', overflow: 'hidden' }}
                scrolling='no'
                frameBorder='0'
                allowFullScreen={true}
                allow='autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share'
              ></iframe>
            </div>
            <div className='fb-post-card'>
              <iframe
                src='https://www.facebook.com/plugins/post.php?href=https%3A%2F%2Fwww.facebook.com%2Fnidhamehtaj%2Fposts%2F4239327016394025&show_text=true&width=500'
                width='100%'
                height='400'
                style={{ border: 'none', overflow: 'hidden' }}
                scrolling='no'
                frameBorder='0'
                allowFullScreen={true}
                allow='autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share'
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* 9. Global Vision Local Expertise with Stats */}
      <section className='global-vision'>
        <div className='container'>
          <div className='content-wrapper'>
            <div className='text-content'>
              <div className='badge'>Excellence in Education</div>
              <h2>We provide the best service</h2>
              <h3>Global Vision Local Expertise</h3>
              <p>
                At DestNation, we blend a global vision with personalized local expertise. Benefit
                from our insights tailored to your unique educational aspirations. Our team's
                in-depth understanding of international trends and local nuances ensures that you
                receive the most relevant guidance for your academic journey.
              </p>
              <div className='features-list'>
                <div className='feature-item'>
                  <i className='fas fa-check-circle'></i>
                  <span>Personalized Guidance</span>
                </div>
                <div className='feature-item'>
                  <i className='fas fa-check-circle'></i>
                  <span>Global Network</span>
                </div>
                <div className='feature-item'>
                  <i className='fas fa-check-circle'></i>
                  <span>Expert Counselors</span>
                </div>
              </div>
            </div>
            <div className='stats-content'>
              <div className='stats-grid'>
                <div className='stat-item'>
                  <div className='stat-icon'>
                    <i className='fas fa-calendar-alt'></i>
                  </div>
                  <div className='stat-number' data-target='24'>
                    0
                  </div>
                  <div className='stat-label'>Years of Experience</div>
                </div>
                <div className='stat-item'>
                  <div className='stat-icon'>
                    <i className='fas fa-users'></i>
                  </div>
                  <div className='stat-number' data-target='1000'>
                    0
                  </div>
                  <div className='stat-suffix'>+</div>
                  <div className='stat-label'>Students Enrolled</div>
                </div>
                <div className='stat-item'>
                  <div className='stat-icon'>
                    <i className='fas fa-user-md'></i>
                  </div>
                  <div className='stat-number' data-target='300'>
                    0
                  </div>
                  <div className='stat-suffix'>+</div>
                  <div className='stat-label'>Doctors Graduated</div>
                </div>
                <div className='stat-item'>
                  <div className='stat-icon'>
                    <i className='fas fa-university'></i>
                  </div>
                  <div className='stat-number' data-target='35'>
                    0
                  </div>
                  <div className='stat-suffix'>+</div>
                  <div className='stat-label'>Top Rated Universities</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='floating-elements'>
          <div className='floating-element element-1'></div>
          <div className='floating-element element-2'></div>
          <div className='floating-element element-3'></div>
        </div>
      </section>
    </div>
  );
};

export default Home;
